import { useEffect, useState } from "react";
import api from "../../lib/api";
import { Spinner } from "../../components/Brand";
import { currency, fmtDate } from "../../lib/status";
import { toast } from "sonner";

export default function Refer() {
    const [data, setData] = useState(null);
    useEffect(() => {
        api.get("/referrals/me").then(({ data }) => setData(data));
    }, []);
    if (!data) return <Spinner />;

    const shareLink = `${window.location.origin}/register?ref=${data.code}`;
    const copy = (txt) => {
        navigator.clipboard.writeText(txt);
        toast.success("Copied!");
    };

    const STATUS_COLOR = {
        pending: "#FF9500",
        qualified: "#34C759",
        credited: "#D4AF37",
    };

    return (
        <div className="space-y-10">
            <div className="lx-label text-[#B2912A]">Refer & Earn</div>
            <div className="grid lg:grid-cols-12 gap-8 items-end">
                <div className="lg:col-span-7">
                    <h1 className="font-serif text-4xl sm:text-5xl text-[#180A21] leading-tight">
                        Give <span className="italic lx-gold-text">AED {data.referee_first_month_discount_percent}%</span> off.
                        <br />
                        Earn <span className="lx-gold-text">{currency(data.reward_amount, "AED")}</span> back.
                    </h1>
                    <p className="text-[#555] mt-5 max-w-xl leading-relaxed">
                        Share your personal code below. Friends get {data.referee_first_month_discount_percent}% off their
                        first Luxurio membership, and you earn {currency(data.reward_amount, "AED")} wallet credit the moment
                        they subscribe. No limit. No cap.
                    </p>
                </div>
                <div
                    className="lg:col-span-5 relative overflow-hidden p-7 text-white"
                    style={{
                        background:
                            "linear-gradient(135deg, #180A21 0%, #2B1438 70%, #180A21 100%)",
                    }}
                >
                    <div className="absolute -right-10 -bottom-10 w-60 h-60 rounded-full bg-[#D4AF37]/10 blur-3xl" />
                    <div className="relative z-10">
                        <div className="lx-label text-[#D4AF37] mb-3">Lifetime earned</div>
                        <div className="font-serif text-5xl text-[#D4AF37]">
                            {currency(data.total_earned, "AED")}
                        </div>
                        <div className="grid grid-cols-2 gap-3 mt-6 pt-6 border-t border-white/10">
                            <div>
                                <div className="lx-label text-white/60">Friends joined</div>
                                <div className="font-serif text-2xl">{data.credited_count}</div>
                            </div>
                            <div>
                                <div className="lx-label text-white/60">In progress</div>
                                <div className="font-serif text-2xl">{data.pending_count}</div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {/* Code + Share */}
            <div className="lx-card p-8">
                <div className="grid md:grid-cols-2 gap-8">
                    <div>
                        <div className="lx-label text-[#B2912A] mb-3">Your referral code</div>
                        <div className="flex items-stretch gap-2">
                            <div
                                data-testid="ref-code-box"
                                className="flex-1 font-serif text-3xl text-[#180A21] py-4 px-5 bg-[#FAFAF8] border border-dashed border-[#D4AF37] tracking-wider text-center"
                            >
                                {data.code}
                            </div>
                            <button
                                data-testid="ref-copy-code"
                                onClick={() => copy(data.code)}
                                className="lx-btn-primary px-5 text-sm font-medium"
                            >
                                Copy
                            </button>
                        </div>
                    </div>
                    <div>
                        <div className="lx-label text-[#B2912A] mb-3">Your share link</div>
                        <div className="flex items-stretch gap-2">
                            <div
                                data-testid="ref-link-box"
                                className="flex-1 text-sm text-[#555] py-4 px-4 bg-[#FAFAF8] border border-[#E5E2DC] truncate"
                            >
                                {shareLink}
                            </div>
                            <button
                                data-testid="ref-copy-link"
                                onClick={() => copy(shareLink)}
                                className="lx-btn-gold px-5 text-sm font-medium"
                            >
                                Copy link
                            </button>
                        </div>
                    </div>
                </div>

                <div className="mt-7 pt-7 border-t border-[#F3F2F0] grid sm:grid-cols-3 gap-4">
                    <Step
                        n="1"
                        title="Share your code"
                        body="Send the link to friends and family — by message, email, or anywhere you talk."
                    />
                    <Step
                        n="2"
                        title="They subscribe"
                        body={`Friends pay 50% off their first month (Essential, Premium, or Elite).`}
                    />
                    <Step
                        n="3"
                        title="You earn AED 25"
                        body="Credit lands in your wallet instantly. Use it on bookings or future subscriptions."
                    />
                </div>
            </div>

            {/* Referrals list */}
            <div className="lx-card">
                <div className="p-6 border-b border-[#F3F2F0] lx-label text-[#B2912A]">
                    Your referrals
                </div>
                {data.referrals.length === 0 ? (
                    <div className="p-12 text-center text-[#555]">
                        No referrals yet. Share your code to start earning.
                    </div>
                ) : (
                    <div className="divide-y divide-[#F3F2F0]">
                        {data.referrals.map((r) => (
                            <div
                                key={r.id}
                                className="p-5 flex flex-wrap items-center justify-between gap-3"
                                data-testid={`ref-row-${r.id}`}
                            >
                                <div>
                                    <div className="text-sm text-[#180A21]">{r.referred_email}</div>
                                    <div className="text-xs text-[#555] mt-0.5">
                                        Joined {fmtDate(r.created_at, { hour: undefined, minute: undefined })}
                                    </div>
                                </div>
                                <div className="flex items-center gap-4">
                                    <span
                                        className="text-[10px] uppercase tracking-wider px-2.5 py-1 border"
                                        style={{
                                            color: STATUS_COLOR[r.status],
                                            borderColor: STATUS_COLOR[r.status] + "55",
                                        }}
                                    >
                                        {r.status === "credited" ? "✓ Credited" : r.status}
                                    </span>
                                    {r.status === "credited" && (
                                        <div className="font-serif text-lg text-[#34C759]">
                                            +{currency(r.reward_amount, "AED")}
                                        </div>
                                    )}
                                </div>
                            </div>
                        ))}
                    </div>
                )}
            </div>
        </div>
    );
}

function Step({ n, title, body }) {
    return (
        <div>
            <div className="font-serif text-3xl text-[#D4AF37] mb-2">{n}</div>
            <div className="font-medium text-[#180A21] mb-1">{title}</div>
            <div className="text-xs text-[#555] leading-relaxed">{body}</div>
        </div>
    );
}
